// The four design categories (taxonomy v2), shared with the importer and flowrec. v2 catalogs carry
// these slugs on flows, screens and animations; older catalogs carry finer labels, which ALIASES maps.

export const DESIGN_CATEGORIES = [
  { slug: 'onboarding', label: 'Onboarding' },
  { slug: 'paywall', label: 'Paywall & subscription' },
  { slug: 'features', label: 'Features' },
  { slug: 'other', label: 'Other' },
] as const;

export type DesignCategory = (typeof DESIGN_CATEGORIES)[number]['slug'];

export const ALIASES: Record<string, DesignCategory> = {
  onboarding: 'onboarding',
  auth: 'onboarding',
  sign_up: 'onboarding',
  login: 'onboarding',
  permission: 'onboarding',
  permission_dialog: 'onboarding',
  paywall: 'paywall',
  monetization: 'paywall',
  features: 'features',
  home: 'features',
  home_feed: 'features',
  search: 'features',
  discover: 'features',
  core: 'features',
  media_player: 'features',
  player: 'features',
  content: 'features',
  content_list: 'features',
  content_detail: 'features',
  browse: 'features',
  mood_checkin: 'features',
  other: 'other',
  account: 'other',
  profile: 'other',
  settings: 'other',
  empty_state: 'other',
  modal_sheet: 'other',
};

/** True for a v2 slug or an older label that maps to one. */
export const isCategoryLabel = (value: string) => Object.hasOwn(ALIASES, value);

/** A v2 slug, or an older label in v2 terms; anything unknown or missing is Other. */
export function designCategory(value: string | null | undefined): DesignCategory {
  return (value && ALIASES[value]) || 'other';
}

export const categoryLabel = (slug: string) => DESIGN_CATEGORIES.find((c) => c.slug === slug)?.label ?? slug;

/**
 * A screen's category: its own (v2 screens carry one), else its flow's. Older catalogs are grouped
 * by flow category only; their finer screen types decide nothing.
 */
export function screenCategory(screen: { category?: string | null }, flowCategory: string | null | undefined): DesignCategory {
  return designCategory(screen.category ?? flowCategory);
}

/** A category's cover: its first screen of that category, else its first screen. */
export function categoryCover<T extends { category: string }>(category: string, screens: T[]): T | undefined {
  return screens.find((s) => s.category === category) ?? screens[0];
}

/**
 * The flows of each category, in category order then recording order (v2 `no`; older catalogs keep
 * their own order), leaving out empty categories. The app page and Quick look both group this way.
 */
export function groupByCategory<F extends { category?: string | null; no?: number | null }>(flows: F[]) {
  return DESIGN_CATEGORIES.map((c) => ({
    slug: c.slug,
    label: c.label,
    flows: flows.filter((f) => designCategory(f.category) === c.slug).toSorted((a, b) => (a.no ?? 0) - (b.no ?? 0)),
  })).filter((g) => g.flows.length > 0);
}

/**
 * A flow name for people: "pro-paywall" → "Pro paywall"; real names are left as they are. A leading
 * word is dropped only when it is the flow's own category, which its section already says.
 */
export function flowLabel(name: string, category?: string | null): string {
  if (!/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/.test(name)) return name;
  const slug = name.replace(/^\d+[-_]/, '');
  const [first, ...rest] = slug.split(/[-_]+/);
  const words = (rest.length > 0 && category != null && first === designCategory(category) ? rest : [first, ...rest]).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
